import { AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { cn } from "@repo/ui/lib/utils";
import type { CrawlLogEntry } from "@/lib/api";

export type NotificationActionType = "pause" | "cancel" | "view";

export interface NotificationAction {
  label: string;
  action: NotificationActionType;
}

export interface NotificationRowData extends CrawlLogEntry {
  actions?: NotificationAction[];
}

interface AuditNotificationRowProps {
  notification: NotificationRowData;
  variant?: "dropdown" | "audit";
  onDismiss?: (id: string) => void;
  onAction?: (action: NotificationActionType, runId: string) => void;
}

export function AuditNotificationRow({
  notification,
  variant = "audit",
  onDismiss,
  onAction,
}: AuditNotificationRowProps) {
  const isError = notification.level === "error";
  const isWarning = notification.level === "warn";
  const isDropdown = variant === "dropdown";
  const runId = notification.job_run_id || notification.job_runs?.id || null;
  const defaultActions: NotificationAction[] = runId && notification.job_runs?.status === "running"
    ? [
        { label: "View Details", action: "view" },
        { label: "Pause", action: "pause" },
        { label: "Cancel", action: "cancel" },
      ]
    : [];
  const availableActions = notification.actions || defaultActions;

  return (
    <div
      className={cn(
        "border rounded-md flex items-start gap-2",
        isDropdown ? "p-2" : "p-3",
      )}
    >
      {isError ? (
        <AlertCircle
          className={cn(
            "flex-shrink-0 mt-0.5",
            isDropdown ? "w-3.5 h-3.5 text-red-500" : "w-4 h-4 text-red-500",
          )}
        />
      ) : isWarning ? (
        <AlertTriangle
          className={cn(
            "flex-shrink-0 mt-0.5",
            isDropdown ? "w-3.5 h-3.5 text-amber-500" : "w-4 h-4 text-amber-500",
          )}
        />
      ) : (
        <Info
          className={cn(
            "flex-shrink-0 mt-0.5",
            isDropdown ? "w-3.5 h-3.5 text-blue-500" : "w-4 h-4 text-blue-500",
          )}
        />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant="outline" className={cn("uppercase", isDropdown ? "text-[9px]" : "text-[10px]")}>
            {notification.level}
          </Badge>
          {notification.scrape_jobs?.name && (
            <span className="text-xs text-muted-foreground truncate">
              {notification.scrape_jobs.name}
            </span>
          )}
        </div>

        <p className={cn("break-words", isDropdown ? "text-xs" : "text-sm")}>
          {notification.message}
        </p>
        <p className="text-[11px] text-muted-foreground mt-1">
          {new Date(notification.created_at).toLocaleString("en-AU")}
        </p>
        {availableActions.length > 0 && onAction && runId && (
          <div className={cn("flex gap-1 mt-1.5", isDropdown && "flex-wrap")}>
            {availableActions.map((action) => (
              <Button
                key={action.action}
                type="button"
                variant={action.action === "cancel" ? "destructive" : "outline"}
                size="sm"
                className={cn(
                  isDropdown ? "h-5 text-[10px] px-1.5" : "h-6 text-[11px] px-2",
                )}
                onClick={() => onAction(action.action, runId)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {onDismiss && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5 flex-shrink-0"
          onClick={() => onDismiss(notification.id)}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
